import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Building2, Loader2, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import StatusPill from "../merchant/components/StatusPill";
import SLAPill from "./components/SLAPill";

/**
 * Brief 3.4.2 — /admin/pipeline funnel view.
 *
 * Two-column funnel: prospects on the left (pre-onboarding pipeline),
 * merchants on the right (active applications). Click destinations:
 *   • Prospect with no merchant_id  → /admin/prospects/{id}
 *   • Prospect already linked       → /admin/merchants/{merchant_id}
 *   • Merchant row                  → /admin/merchants/{merchant_id}
 *
 * No analytics in 3.4.2 (deferred to P2 once ≥10 prospects funneled OR
 * ≥3 merchants live). This view just lists rows so the founder can scan
 * the whole deal flow on one page.
 */

const PROSPECT_STATUS_LABELS = {
  interested: "Interested",
  discovery: "Discovery",
  qualifying: "Qualifying",
  ready_for_onboarding: "Ready",
  onboarding_link_sent: "Link sent",
};

const PROSPECT_STATUS_TONES = {
  interested:           "bg-zinc-500/[0.08] text-zinc-300 border-zinc-500/30",
  discovery:            "bg-sky-500/[0.10] text-sky-200 border-sky-500/30",
  qualifying:           "bg-violet-500/[0.10] text-violet-200 border-violet-500/30",
  ready_for_onboarding: "bg-amber-400/[0.10] text-amber-200 border-amber-400/30",
  onboarding_link_sent: "bg-emerald-400/[0.10] text-emerald-200 border-emerald-400/30",
};

function ProspectStatusPill({ status }) {
  const cls = PROSPECT_STATUS_TONES[status] || "bg-white/[0.04] text-zinc-400 border-white/[0.08]";
  return (
    <span
      data-testid={`prospect-status-${status}`}
      className={`inline-flex items-center rounded-full border uppercase tracking-[0.14em] text-[10px] px-2 py-0.5 ${cls}`}
    >
      {PROSPECT_STATUS_LABELS[status] || status}
    </span>
  );
}

export default function AdminPipeline() {
  const [data, setData] = useState({ prospects: [], merchants: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const aliveRef = useRef(true);

  // Brief 3.13 (Feb 25 2026) — page was stale because the list only
  // fetched once on mount. Now: refetch on window focus, on a 60s
  // interval, and on explicit click of the Refresh button. AdminPipeline
  // is the AM's most-visited triage view; staleness here directly
  // translates into "where did this merchant go?" support tickets.
  const fetchPipeline = useCallback(async ({ silent } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const { data: body } = await api.get("/admin/pipeline");
      if (aliveRef.current) {
        setData(body);
        setLastFetched(new Date());
      }
    } finally {
      if (aliveRef.current) {
        setRefreshing(false);
        setLoading(false);
      }
    }
  }, []);

  
  useEffect(() => {
    aliveRef.current = true;
    fetchPipeline({ silent: true });
    const onFocus = () => fetchPipeline({ silent: true });
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      fetchPipeline({ silent: true });
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    const interval = setInterval(() => fetchPipeline({ silent: true }), 60_000);
    return () => {
      aliveRef.current = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(interval);
    };
  }, [fetchPipeline]);
  

  const prospectCounts = data.counts?.prospects_by_status || {};
  const merchantCounts = data.counts?.merchants_by_status || {};
  const totalProspects = Object.values(prospectCounts).reduce((a, b) => a + b, 0);
  const totalMerchants = Object.values(merchantCounts).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="admin-pipeline-page" className="max-w-7xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Pipeline</div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
            Deal pipeline
          </h1>
          <p className="mt-2 text-[13px] text-zinc-400">
            {totalProspects} prospect{totalProspects === 1 ? "" : "s"} · {totalMerchants} merchant{totalMerchants === 1 ? "" : "s"} in flight
            <span className="text-zinc-600"> · terminal & live hidden</span>
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-zinc-500" data-testid="pipeline-refresh-controls">
          {lastFetched && (
            <span data-testid="pipeline-last-fetched">
              Updated {lastFetched.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
          <button
            type="button"
            onClick={() => fetchPipeline()}
            disabled={refreshing}
            data-testid="pipeline-refresh-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/[0.20] text-white disabled:opacity-50 px-3 py-1.5 text-[11.5px] transition-colors"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-center text-zinc-500 text-[12px] uppercase tracking-[0.22em] inline-flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Prospects column */}
          <section
            data-testid="pipeline-prospects-section"
            className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden"
          >
            <header className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-3.5 w-3.5 text-zinc-500" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-zinc-400">Prospects</span>
              </div>
              <span className="text-[11px] text-zinc-600 ref-mono">{data.prospects.length}</span>
            </header>
            {data.prospects.length === 0 ? (
              <div className="px-5 py-10 text-center text-zinc-600 text-[12.5px]">
                No active prospects. Track one via Nerea in Ops DM.
              </div>
            ) : (
              <ul>
                {data.prospects.map((p) => {
                  const target = p.merchant_id
                    ? `/admin/merchants/${p.merchant_id}`
                    : `/admin/prospects/${p.prospect_id}`;
                  return (
                    <li
                      key={p.prospect_id}
                      data-testid={`pipeline-prospect-${p.prospect_id}`}
                      className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02] transition-colors"
                    >
                      <Link to={target} className="flex items-center justify-between px-5 py-3.5">
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] text-white truncate">{p.name}</div>
                          <div className="text-[11.5px] text-zinc-500 truncate">
                            {p.vertical || "—"}
                            {p.merchant_id && (
                              <span className="ml-2 text-emerald-300/70 ref-mono text-[10.5px]">
                                · linked
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 ml-3 shrink-0">
                          <ProspectStatusPill status={p.status} />
                          <ArrowRight className="h-3 w-3 text-zinc-600" />
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Merchants column */}
          <section
            data-testid="pipeline-merchants-section"
            className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden"
          >
            <header className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5 text-zinc-500" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-zinc-400">Merchants</span>
              </div>
              <span className="text-[11px] text-zinc-600 ref-mono">{data.merchants.length}</span>
            </header>
            {data.merchants.length === 0 ? (
              <div className="px-5 py-10 text-center text-zinc-600 text-[12.5px]">
                No active merchant applications.
              </div>
            ) : (
              <ul>
                {data.merchants.map((m) => (
                  <li
                    key={m.merchant_id}
                    data-testid={`pipeline-merchant-${m.merchant_id}`}
                    className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02] transition-colors"
                  >
                    <Link
                      to={`/admin/merchants/${m.merchant_id}`}
                      className="flex items-center justify-between px-5 py-3.5"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] text-white truncate">{m.legal_name || "—"}</div>
                        <div className="text-[11.5px] text-zinc-500 ref-mono">{m.ref}</div>
                      </div>
                      <div className="flex items-center gap-2 ml-3 shrink-0">
                        {m.sla && m.sla.state !== "not_applicable" && (
                          <SLAPill sla={m.sla} size="sm" testId={`pipeline-sla-${m.merchant_id}`} />
                        )}
                        <StatusPill status={m.status} size="sm" />
                        <ArrowRight className="h-3 w-3 text-zinc-600" />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
